// here it is created array of objects
export const services = [
    {
        "id": "svc_8b9a3d3a-2f1e-4d1e-9b60-0e7fb62a5f3d",
        "slug": "web-development",
        "name": "Web Development",
        "summary": "Responsive, fast, and secure websites and web apps built to scale your business.",
        "hero": {
            "title": "From idea to launch—fast, secure, scalable web apps",
            "pitch": "We design, build, and ship modern web experiences with performance, accessibility, and ROI baked in."
        },
        "icon": "icons/web-dev.svg",
        "illustration": "images/services/web-dev-hero.webp",
        "problemStatement": "Slow, inconsistent sites lose users and limit growth. Teams struggle with maintainability and time-to-market.",
        "valueProposition": [
            "Measured performance (Core Web Vitals) and SEO improvements",
            "Maintainable code and design systems for rapid iteration",
            "Security, analytics, and CI/CD from day one"
        ],
        "deliverables": [
            "Technical architecture & roadmap",
            "Responsive UI and component library",
            "Frontend + backend implementation",
            "Content model & CMS setup",
            "Automated tests & CI/CD pipeline",
            "Analytics, monitoring, and documentation"
        ],
        "features": [
            { "title": "Performance-first", "description": "Lighthouse 90+ targets and optimized Core Web Vitals." },
            { "title": "SEO-ready", "description": "Clean URLs, structured data, and meta automation." },
            { "title": "Accessible by default", "description": "WCAG 2.2 AA checks and audits." },
            { "title": "Scalable architecture", "description": "API-first, modular services, and cloud-native deploys." }
        ],
        "process": [
            { "step": 1, "name": "Discovery", "description": "Goals, users, and scope; define KPIs.", "duration": "1–2 weeks" },
            { "step": 2, "name": "Architecture & UX", "description": "System design, data model, low-fi flows.", "duration": "1–2 weeks" },
            { "step": 3, "name": "Build", "description": "Iterative sprints: frontend, backend, integration.", "duration": "3–8 weeks" },
            { "step": 4, "name": "QA & Hardening", "description": "Automated tests, a11y, perf, security review.", "duration": "1–2 weeks" },
            { "step": 5, "name": "Launch & Handover", "description": "Monitoring, docs, training.", "duration": "1 week" }
        ],
        "techStack": [
            { "category": "Frontend", "tools": ["React", "Next.js", "Vue", "TypeScript"] },
            { "category": "Backend", "tools": ["Node.js", "Express", "NestJS"] },
            { "category": "CMS", "tools": ["Sanity", "Strapi", "Contentful"] },
            { "category": "DB", "tools": ["PostgreSQL", "MongoDB", "Prisma"] },
            { "category": "Infra", "tools": ["Vercel", "AWS", "Docker", "CI/CD"] },
            { "category": "Testing", "tools": ["Jest", "Playwright", "Lighthouse CI"] }
        ],
        "pricingModel": "time_and_materials",
        "startingFrom": 4000,
        "timelineEstimate": { "minWeeks": 6, "maxWeeks": 14 },
        "teamRoles": ["Product Manager", "Tech Lead", "Frontend Dev", "Backend Dev", "QA Engineer", "DevOps"],
        "industries": ["SaaS", "E-commerce", "Education", "Healthcare"],
        "requirements": [
            "Brand assets and design guidelines (if any)",
            "Primary user personas and top tasks",
            "Content sources and integrations",
            "Stakeholder availability for weekly reviews"
        ],
        "successMetrics": [
            "Time to Interactive (TTI) and LCP under 2.5s",
            "Conversion rate uplift",
            "Organic traffic growth",
            "Issue escape rate < 2%"
        ],
        "caseStudies": ["cs_web_001", "cs_web_007"],
        "testimonials": ["t_web_01", "t_web_05"],
        "faqs": [
            { "q": "Do you migrate existing sites?", "a": "Yes—content and redirects with zero-downtime strategies." },
            { "q": "Can you work with our in-house team?", "a": "Absolutely—co-build with shared repos and rituals." }
        ],
        "compliance": {
            "privacy": ["GDPR"],
            "accessibility": "WCAG 2.2 AA baseline",
            "security": ["OWASP ASVS checks", "Dependency scanning"]
        },
        "maintenance": {
            "plans": [
                { "name": "Basic", "monthlyHours": 10, "sla": "48h response" },
                { "name": "Growth", "monthlyHours": 30, "sla": "24h response" }
            ]
        },
        "cta": { "label": "Get a Web Dev Quote", "target": "/contact?service=web-development" },
        "seo": {
            "title": "Web Development Services | YourCompany",
            "metaDescription": "We build fast, secure, accessible websites and web apps that grow your business."
        },
        "schemaOrg": {
        },
        "components": [
            { "name": "User Interface", "description": "Responsive and interactive frontend components for seamless user experience.", "technologies": ["React", "Next.js", "TypeScript", "CSS"] },
            { "name": "Backend API", "description": "Scalable server-side logic handling data processing and integrations.", "technologies": ["Node.js", "Express", "NestJS"] },
            { "name": "Database Layer", "description": "Efficient data storage and retrieval with ORM support.", "technologies": ["PostgreSQL", "MongoDB", "Prisma"] }
        ],
        "images": [
            { "url": "https://images.pexels.com/photos/39284/macbook-apple-imac-computer-39284.jpeg", "description": "A screenshot showcasing our web development portfolio and recent projects." },
            { "url": "https://images.pexels.com/photos/1181467/pexels-photo-1181467.jpeg", "description": "Team collaboration on innovative web solutions and client presentations." }
        ],
    },
    {
        "id": "svc_5a2f1b4e-a3d7-4a9b-9b2a-6f0f5e1c9d11",
        "slug": "mobile-development",
        "name": "Mobile App Development",
        "summary": "High-performance iOS and Android apps with smooth UX, offline reliability, and secure APIs.",
        "hero": {
            "title": "Delightful mobile apps, shipped reliably",
            "pitch": "We design and build native and cross-platform apps with performance, accessibility, and analytics baked in—from MVP to scale."
        },
        "icon": "icons/mobile-dev.svg",
        "illustration": "images/services/mobile-dev-hero.webp",
        "problemStatement": "Users churn on slow, crash-prone apps. Device fragmentation and store policies make shipping risky without the right process.",
        "valueProposition": [
            "Native feel and performance across iOS and Android",
            "Battle-tested release process (CI/CD, staged rollouts, OTA updates)",
            "Privacy-first analytics and growth tooling"
        ],
        "deliverables": [
            "Product spec & technical architecture",
            "Figma flows & platform-specific UI kits",
            "App implementation (iOS, Android, or cross-platform)",
            "API integration & auth",
            "Push notifications & deep links",
            "Offline cache & sync strategy",
            "Automated tests (unit, UI, E2E) & device matrix",
            "CI/CD pipelines with staging & review builds",
            "App Store / Play Console assets & listings",
            "Monitoring setup (crash & performance) and docs"
        ],
        "features": [
            { "title": "Offline-first", "description": "Robust caching, background sync, and conflict handling." },
            { "title": "Secure by default", "description": "Keychain/Secure Storage, TLS pinning options, OWASP MAS checks." },
            { "title": "Growth & engagement", "description": "Push, in-app messaging, deep links, A/B testing, feature flags." },
            { "title": "Observability", "description": "Crash-free sessions, cold-start tracking, ANR monitoring." }
        ],
        "process": [
            { "step": 1, "name": "Discovery", "description": "Goals, users, success metrics, platform choice.", "duration": "1–2 weeks" },
            { "step": 2, "name": "UX & Architecture", "description": "Flows, UI kit, data model, offline & auth strategy.", "duration": "1–2 weeks" },
            { "step": 3, "name": "Build", "description": "Sprints across features, integrations, and device hardening.", "duration": "4–10 weeks" },
            { "step": 4, "name": "QA & Beta", "description": "Automated tests, device lab, TestFlight/Internal testing.", "duration": "2–3 weeks" },
            { "step": 5, "name": "Launch & Scale", "description": "Store submission, staged rollout, monitoring & iteration.", "duration": "1 week" }
        ],
        "techStack": [
            { "category": "Platforms", "tools": ["iOS", "Android"] },
            { "category": "Native", "tools": ["Swift", "SwiftUI", "Kotlin", "Jetpack Compose"] },
            { "category": "Cross-platform", "tools": ["React Native", "Flutter"] },
            { "category": "Backend/API", "tools": ["Node.js", "NestJS", "GraphQL", "REST", "Firebase/Supabase"] },
            { "category": "Data", "tools": ["SQLite/Room", "Core Data", "Realm"] },
            { "category": "CI/CD", "tools": ["fastlane", "GitHub Actions", "Bitrise", "Codemagic"] },
            { "category": "Testing", "tools": ["JUnit", "XCTest", "Detox", "Espresso", "Playwright (webviews)"] },
            { "category": "Monitoring", "tools": ["Firebase Crashlytics", "Sentry", "Datadog RUM"] },
            { "category": "Analytics", "tools": ["Firebase Analytics", "Segment", "Amplitude"] }
        ],
        "pricingModel": "time_and_materials",
        "startingFrom": 6000,
        "timelineEstimate": { "minWeeks": 8, "maxWeeks": 18 },
        "teamRoles": [
            "Product Manager",
            "Mobile Tech Lead",
            "iOS Engineer",
            "Android Engineer",
            "Cross-platform Engineer",
            "QA Engineer",
            "DevOps / Release Engineer"
        ],
        "industries": ["Fintech", "E-commerce", "Healthcare", "On-Demand", "Education"],
        "requirements": [
            "App goals, user personas, and core journeys",
            "Brand assets and copy guidelines",
            "API access or backend specs",
            "Apple/Google developer accounts for submission",
            "Stakeholder availability for sprint reviews"
        ],
        "successMetrics": [
            "Crash-free sessions ≥ 99.5%",
            "Cold start < 2s on target devices",
            "D1/D7 retention uplift",
            "MAU/WAU growth and feature adoption",
            "Store rating ≥ 4.5★"
        ],
        "caseStudies": ["cs_mobile_003", "cs_mobile_009"],
        "testimonials": ["t_mobile_02", "t_mobile_06"],
        "faqs": [
            { "q": "Native or cross-platform?", "a": "We recommend based on feature set, performance needs, and team skills. We support Swift/Kotlin, React Native, and Flutter." },
            { "q": "Do you handle store submission?", "a": "Yes—listings, screenshots, privacy labels, and compliance checks. We also guide staged rollouts." },
            { "q": "Can you integrate existing APIs?", "a": "Absolutely—REST/GraphQL with caching, pagination, and auth." }
        ],
        "compliance": {
            "privacy": ["GDPR", "Apple ATT prompts when applicable"],
            "security": ["OWASP MAS baseline", "Dependency & supply-chain scanning"],
            "platform": ["App Store Review Guidelines", "Google Play Developer Program Policies"],
            "accessibility": "iOS/Android accessibility APIs; WCAG-aligned practices"
        },
        "risksAndMitigations": [
            { "risk": "Store rejections", "mitigation": "Pre-submission checklists, policy review, and test builds." },
            { "risk": "Device fragmentation", "mitigation": "Target device matrix, automated UI tests, and staged rollout." },
            { "risk": "Performance regressions", "mitigation": "Perf budgets, profiling, and CI checks." }
        ],
        "maintenance": {
            "plans": [
                { "name": "Essentials", "monthlyHours": 12, "includes": ["OS updates", "Crash triage", "Minor fixes"], "sla": "48h response" },
                { "name": "Growth", "monthlyHours": 32, "includes": ["Feature flags/A-B tests", "Experimentation", "ASO refresh"], "sla": "24h response" }
            ],
            "releaseStrategy": "Monthly stable with hotfixes; staged rollouts; OTA updates where supported."
        },
        "packages": [
            {
                "name": "MVP Sprint",
                "summary": "Core flows + analytics in 6–8 weeks.",
                "includes": ["Auth", "Core feature", "Crash & analytics", "Beta via TestFlight/Internal"],
                "excludes": ["Complex offline", "Payments", "Custom animations"]
            },
            {
                "name": "Scale-Up",
                "summary": "Hardening, growth loops, and monitoring.",
                "includes": ["Perf pass", "A/B testing", "Feature flags", "Staged rollout"],
                "excludes": []
            }
        ],
        "cta": { "label": "Get a Mobile App Quote", "target": "/contact?service=mobile-development" },
        "seo": {
            "title": "Mobile App Development Services | YourCompany",
            "metaDescription": "Native and cross-platform iOS/Android apps with performance, security, and growth built in."
        },
        "schemaOrg": { "@type": "Service", "serviceType": "Mobile Application Development" },
        "locales": {
            "de-DE": {
                "name": "Mobile Entwicklung",
                "summary": "Leistungsstarke iOS- & Android-Apps mit erstklassiger UX."
            }
        },
        "relatedServices": ["ui-ux-design", "web-development", "data-analytics"],
        "status": "active",
        "version": "1.0.0",
        "lastUpdated": "2025-10-27",
        "components": [
            { "name": "Native UI Components", "description": "Platform-specific interfaces for iOS and Android with native performance.", "technologies": ["Swift", "SwiftUI", "Kotlin", "Jetpack Compose"] },
            { "name": "Cross-Platform Logic", "description": "Shared business logic for hybrid apps using React Native or Flutter.", "technologies": ["React Native", "Flutter", "JavaScript"] },
            { "name": "Offline Data Sync", "description": "Local storage and synchronization with remote servers.", "technologies": ["SQLite", "Realm", "Firebase"] }
        ],
        "images": [
            { "url": "https://images.pexels.com/photos/887751/pexels-photo-887751.jpeg?auto=compress&cs=tinysrgb&w=600", "description": "A mobile app interface showcasing user-friendly design and functionality." },
            { "url": "https://images.pexels.com/photos/3912958/pexels-photo-3912958.jpeg", "description": "Smartphone displaying our mobile development work and innovations." },
             
        ]
    
    },
    {
        "id": "svc_uiux_7f3f4b21-1a4b-4f9b-8a7e-0b1c2d3e4f55",
        "slug": "ui-ux-design",
        "name": "UI/UX Design",
        "summary": "Research-driven product design: clear flows, accessible interfaces, and a scalable design system.",
        "hero": {
            "title": "Design that elevates outcomes",
            "pitch": "We craft intuitive, accessible interfaces backed by research—then systematize them so your team can move fast."
        },
        "icon": "icons/ui-ux.svg",
        "illustration": "images/services/ui-ux-hero.webp",
        "problemStatement": "Users get lost in clunky flows and inconsistent UI. Teams ship slower without a shared design language.",
        "valueProposition": [
            "User research that de-risks roadmaps",
            "Design systems that scale across products",
            "Accessibility and usability baked into every screen"
        ],
        "deliverables": [
            "Research plan & interviews",
            "Personas & journey maps",
            "Information architecture & user flows",
            "Wireframes & interactive prototypes",
            "Visual design & motion specs",
            "Design system (tokens, components, guidelines)",
            "Usability test reports",
            "Dev-ready handoff (specs & annotations)"
        ],
        "features": [
            { "title": "Research-first", "description": "Qual/quant insights guide decisions—not hunches." },
            { "title": "Accessible by default", "description": "WCAG 2.2 AA checks, color contrast, keyboard flows." },
            { "title": "Systemized", "description": "Tokens, components, and usage guidelines to scale." },
            { "title": "Measurable", "description": "Design tied to KPIs like conversion and task success." }
        ],
        "process": [
            { "step": 1, "name": "Discover", "description": "Stakeholder goals, user interviews, analytics review.", "duration": "1–2 weeks" },
            { "step": 2, "name": "Define", "description": "Personas, journeys, IA, success metrics.", "duration": "1 week" },
            { "step": 3, "name": "Design", "description": "Wireframes → hi-fi UI → prototypes & motion.", "duration": "2–4 weeks" },
            { "step": 4, "name": "Validate", "description": "Usability tests, iteration, a11y review.", "duration": "1–2 weeks" },
            { "step": 5, "name": "Handoff", "description": "Specs, tokens, Storybook entries, paired dev reviews.", "duration": "1 week" }
        ],
        "techStack": [
            { "category": "Design", "tools": ["Figma", "FigJam", "Adobe CC"] },
            { "category": "Prototyping & Motion", "tools": ["Figma Prototyping", "After Effects", "Lottie"] },
            { "category": "Research & Testing", "tools": ["Maze", "Lookback", "Useberry", "Hotjar"] },
            { "category": "Design System", "tools": ["Design Tokens", "Storybook", "Chromatic"] },
            { "category": "Accessibility", "tools": ["axe", "Contrast", "Screen readers (NVDA/VoiceOver)"] }
        ],
        "pricingModel": "time_and_materials",
        "startingFrom": 3000,
        "timelineEstimate": { "minWeeks": 4, "maxWeeks": 10 },
        "teamRoles": ["Product Designer", "UX Researcher", "Design Systems Lead", "UX Writer", "Front-end Engineer"],
        "industries": ["SaaS", "Fintech", "E-commerce", "Healthcare", "Education"],
        "requirements": [
            "Business goals & KPIs",
            "Target users and access for research",
            "Brand guidelines (if available)",
            "Existing analytics or product data"
        ],
        "successMetrics": [
            "Task success rate ↑",
            "SUS score ≥ 80",
            "Conversion / completion rate ↑",
            "Support tickets on key flows ↓"
        ],
        "caseStudies": ["cs_uiux_010", "cs_uiux_013"],
        "testimonials": ["t_uiux_01", "t_uiux_03"],
        "faqs": [
            { "q": "Do you test with real users?", "a": "Yes—remote or in-person moderated sessions plus rapid unmoderated tests." },
            { "q": "Can you create a design system?", "a": "Absolutely—tokens, components, and docs integrated with Storybook." }
        ],
        "risksAndMitigations": [
            { "risk": "Stakeholder misalignment", "mitigation": "Design reviews with success criteria and decision logs." },
            { "risk": "Scope creep", "mitigation": "Prioritized roadmap and change-control rituals." }
        ],
        "maintenance": {
            "plans": [
                { "name": "System Care", "monthlyHours": 8, "includes": ["Token upkeep", "Component fixes"], "sla": "72h response" },
                { "name": "Design Ops", "monthlyHours": 24, "includes": ["New components", "A/B test variants"], "sla": "24h response" }
            ]
        },
        "cta": { "label": "Start a Design Sprint", "target": "/contact?service=ui-ux-design" },
        "seo": {
            "title": "UI/UX Design Services | YourCompany",
            "metaDescription": "Research-driven product design, usability testing, and design systems that scale."
        },
        "schemaOrg": { "@type": "Service", "serviceType": "UI/UX Design" },
        "locales": {
            "de-DE": { "name": "UI/UX Design", "summary": "Forschergetriebene Produktgestaltung & skalierbare Designsysteme." }
        },
        "relatedServices": ["web-development", "mobile-development", "branding"],
        "status": "active",
        "version": "1.0.0",
        "lastUpdated": "2025-10-27",
        "components": [
            { "name": "User Research Tools", "description": "Conducting interviews and usability tests to gather insights.", "technologies": ["Figma", "Maze", "Hotjar"] },
            { "name": "Design System", "description": "Reusable components and tokens for consistent UI.", "technologies": ["Figma", "Storybook", "Design Tokens"] },
            { "name": "Prototyping", "description": "Interactive mockups for validating user flows.", "technologies": ["Figma Prototyping", "After Effects"] }
        ],
        "images": [
            { "url": "https://images.pexels.com/photos/221185/pexels-photo-221185.jpeg", "description": "UI design example showcasing modern interfaces and user-centric layouts." },
            { "url": "https://images.pexels.com/photos/326518/pexels-photo-326518.jpeg", "description": "UX research process involving user interviews and usability testing." },
           
        ]
    },
    {
        "id": "svc_ml_4c8a92d3-bd71-4e33-98e2-f42d8b6a1c93",
        "slug": "machine-learning",
        "name": "Machine Learning",
        "summary": "Data-driven intelligence: predictive models, automation, and AI solutions tailored to your business goals.",
        "hero": {
            "title": "Unlock the Power of Intelligent Automation",
            "pitch": "We design and deploy machine learning models that turn your data into insights, predictions, and smarter decisions."
        },
        "icon": "icons/machine-learning.svg",
        "illustration": "images/services/machine-learning-hero.webp",
        "problemStatement": "Businesses collect massive amounts of data but struggle to extract actionable insights or automate decisions effectively.",
        "valueProposition": [
            "Custom ML models trained on your data",
            "End-to-end AI pipeline development",
            "Explainable AI and transparent metrics",
            "Automation that reduces operational costs"
        ],
        "deliverables": [
            "Data collection & preprocessing scripts",
            "Exploratory data analysis report",
            "Model design & training documentation",
            "Model evaluation & validation report",
            "Deployment-ready ML pipeline",
            "Monitoring & retraining plan",
            "Integration with existing systems",
            "Performance dashboards & APIs"
        ],
        "features": [
            { "title": "Custom ML Models", "description": "Tailored models for prediction, classification, and recommendation." },
            { "title": "Explainable AI", "description": "Interpretability tools for transparent decision-making." },
            { "title": "Scalable Infrastructure", "description": "Cloud-ready pipelines built on AWS, GCP, or Azure." },
            { "title": "Continuous Learning", "description": "Feedback loops for model retraining and performance optimization." }
        ],
        "process": [
            { "step": 1, "name": "Discover", "description": "Business goals, data audit, and feasibility study.", "duration": "1–2 weeks" },
            { "step": 2, "name": "Prepare", "description": "Data cleaning, feature engineering, and labeling.", "duration": "1–3 weeks" },
            { "step": 3, "name": "Model", "description": "Model selection, training, tuning, and validation.", "duration": "2–4 weeks" },
            { "step": 4, "name": "Deploy", "description": "Pipeline setup, API integration, and cloud deployment.", "duration": "1–2 weeks" },
            { "step": 5, "name": "Monitor", "description": "Performance tracking, retraining, and reporting.", "duration": "Ongoing" }
        ],
        "techStack": [
            { "category": "Languages", "tools": ["Python", "R", "SQL"] },
            { "category": "Frameworks & Libraries", "tools": ["TensorFlow", "PyTorch", "Scikit-learn", "XGBoost"] },
            { "category": "Data Processing", "tools": ["Pandas", "NumPy", "Apache Spark", "Airflow"] },
            { "category": "Visualization & Dashboards", "tools": ["Tableau", "Power BI", "Matplotlib", "Plotly"] },
            { "category": "Deployment & Cloud", "tools": ["AWS SageMaker", "GCP AI Platform", "Azure ML", "Docker", "Kubernetes"] }
        ],
        "pricingModel": "time_and_materials",
        "startingFrom": 5000,
        "timelineEstimate": { "minWeeks": 6, "maxWeeks": 14 },
        "teamRoles": ["Data Scientist", "ML Engineer", "Data Engineer", "AI Researcher", "Project Manager"],
        "industries": ["Finance", "Healthcare", "E-commerce", "Manufacturing", "Technology"],
        "requirements": [
            "Access to relevant datasets",
            "Business KPIs and problem definition",
            "Infrastructure details (cloud/on-prem)",
            "Security and compliance requirements"
        ],
        "successMetrics": [
            "Prediction accuracy ↑",
            "Operational efficiency ↑",
            "Error rate ↓",
            "Time-to-decision ↓"
        ],
        "caseStudies": ["cs_ml_001", "cs_ml_005"],
        "testimonials": ["t_ml_01", "t_ml_02"],
        "faqs": [
            { "q": "Can you work with our existing data systems?", "a": "Yes, we integrate seamlessly with your current databases, APIs, and data pipelines." },
            { "q": "Do you handle model deployment?", "a": "Absolutely. We provide full deployment to cloud or on-prem environments with continuous monitoring." }
        ],
        "risksAndMitigations": [
            { "risk": "Poor data quality", "mitigation": "Comprehensive preprocessing and data validation checks." },
            { "risk": "Model drift over time", "mitigation": "Scheduled retraining and automated performance monitoring." }
        ],
        "maintenance": {
            "plans": [
                { "name": "Model Care", "monthlyHours": 10, "includes": ["Model health monitoring", "Retraining support"], "sla": "48h response" },
                { "name": "AI Ops", "monthlyHours": 30, "includes": ["New model versions", "Data drift analysis", "Pipeline updates"], "sla": "24h response" }
            ]
        },
        "cta": { "label": "Start Your AI Project", "target": "/contact?service=machine-learning" },
        "seo": {
            "title": "Machine Learning Services | YourCompany",
            "metaDescription": "Build intelligent systems with custom machine learning models, automation, and data-driven insights."
        },
        "schemaOrg": { "@type": "Service", "serviceType": "Machine Learning" },
        "locales": {
            "de-DE": { "name": "Maschinelles Lernen", "summary": "Datengetriebene KI-Lösungen & intelligente Automatisierung für Ihr Unternehmen." }
        },
        "relatedServices": ["data-science", "ai-consulting", "cloud-engineering"],
        "status": "active",
        "version": "1.0.0",
        "lastUpdated": "2025-10-27",
        "components": [
            { "name": "Model Training Pipeline", "description": "Automated workflows for data preprocessing and model training.", "technologies": ["Python", "TensorFlow", "Scikit-learn"] },
            { "name": "Prediction API", "description": "RESTful endpoints for real-time model inference.", "technologies": ["Flask", "FastAPI", "Docker"] },
            { "name": "Monitoring Dashboard", "description": "Visualizing model performance and drift detection.", "technologies": ["Grafana", "Prometheus", "Plotly"] }
        ],
        "images": [
            { "url": "https://images.pexels.com/photos/2239655/pexels-photo-2239655.jpeg", "description": "Machine learning visualization and data processing." },
            { "url": "https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg", "description": "AI and neural network concepts in action." },
        ],
    },
    {
        "id": "svc_cloud_9e4f5d2a-8b7c-4d9e-9f1a-2c3d4e5f6g78",
        "slug": "cloud-computing",
        "name": "Cloud Computing",
        "summary": "Scalable cloud infrastructure and migration services for modern businesses.",
        "hero": {
            "title": "Scale with Confidence in the Cloud",
            "pitch": "We design, migrate, and optimize cloud architectures that are secure, cost-effective, and future-proof."
        },
        "icon": "icons/cloud-computing.svg",
        "illustration": "images/services/cloud-computing-hero.webp",
        "problemStatement": "Legacy systems are expensive to maintain, insecure, and can't scale with business growth.",
        "valueProposition": [
            "Cost-optimized cloud architectures",
            "Secure, compliant infrastructure",
            "Seamless migration with zero downtime"
        ],
        "deliverables": [
            "Cloud architecture design & roadmap",
            "Migration strategy & execution plan",
            "Infrastructure as Code (IaC) setup",
            "Security & compliance configuration",
            "Monitoring & cost optimization",
            "Training & documentation"
        ],
        "features": [
            { "title": "Multi-Cloud Support", "description": "AWS, Azure, GCP—hybrid or multi-cloud strategies." },
            { "title": "Auto-Scaling", "description": "Infrastructure that grows with your traffic." },
            { "title": "Cost Optimization", "description": "Reserved instances, spot pricing, and usage analytics." },
            { "title": "Security-First", "description": "Encryption, IAM, compliance (SOC2, HIPAA, etc.)." }
        ],
        "process": [
            { "step": 1, "name": "Assess", "description": "Current infra audit, requirements gathering.", "duration": "1–2 weeks" },
            { "step": 2, "name": "Design", "description": "Architecture, cost modeling, security plan.", "duration": "1–2 weeks" },
            { "step": 3, "name": "Migrate", "description": "Phased migration, testing, and cutover.", "duration": "2–6 weeks" },
            { "step": 4, "name": "Optimize", "description": "Monitoring, performance tuning, cost review.", "duration": "1–2 weeks" },
            { "step": 5, "name": "Manage", "description": "Ongoing support, updates, and scaling.", "duration": "Ongoing" }
        ],
        "techStack": [
            { "category": "Cloud Providers", "tools": ["AWS", "Azure", "GCP"] },
            { "category": "IaC & Automation", "tools": ["Terraform", "CloudFormation", "Ansible"] },
            { "category": "Containers & Orchestration", "tools": ["Docker", "Kubernetes", "ECS/EKS"] },
            { "category": "Monitoring & Security", "tools": ["CloudWatch", "Datadog", "AWS Config", "GuardDuty"] }
        ],
        "pricingModel": "time_and_materials",
        "startingFrom": 5000,
        "timelineEstimate": { "minWeeks": 6, "maxWeeks": 12 },
        "teamRoles": ["Cloud Architect", "DevOps Engineer", "Security Engineer", "Project Manager"],
        "industries": ["Fintech", "Healthcare", "E-commerce", "SaaS", "Manufacturing"],
        "requirements": [
            "Current infrastructure details",
            "Business requirements & SLAs",
            "Security & compliance needs",
            "Budget constraints"
        ],
        "successMetrics": [
            "Infrastructure costs ↓",
            "Uptime ≥ 99.9%",
            "Time-to-deploy ↓",
            "Security incidents ↓"
        ],
        "caseStudies": ["cs_cloud_001", "cs_cloud_005"],
        "testimonials": ["t_cloud_01", "t_cloud_03"],
        "faqs": [
            { "q": "Do you handle data migration?", "a": "Yes—secure, tested migrations with rollback plans." },
            { "q": "Can you manage multi-cloud setups?", "a": "Absolutely—we design for portability and cost optimization." }
        ],
        "compliance": {
            "standards": ["SOC2", "ISO 27001", "GDPR", "HIPAA"],
            "security": ["Encryption at rest/transit", "IAM best practices", "Regular audits"]
        },
        "maintenance": {
            "plans": [
                { "name": "Cloud Care", "monthlyHours": 16, "includes": ["Monitoring", "Updates", "Cost review"], "sla": "24h response" },
                { "name": "Cloud Ops", "monthlyHours": 40, "includes": ["Scaling", "Security patches", "Performance tuning"], "sla": "12h response" }
            ]
        },
        "cta": { "label": "Start Your Cloud Migration", "target": "/contact?service=cloud-computing" },
        "seo": {
            "title": "Cloud Computing Services | YourCompany",
            "metaDescription": "Secure, scalable cloud infrastructure, migration, and optimization for modern businesses."
        },
        "schemaOrg": { "@type": "Service", "serviceType": "Cloud Computing" },
        "locales": {
            "de-DE": { "name": "Cloud Computing", "summary": "Skalierbare Cloud-Infrastruktur & sichere Migrationen." }
        },
        "relatedServices": ["web-development", "data-analytics", "security"],
        "status": "active",
        "version": "1.0.0",
        "lastUpdated": "2025-10-27",
        "components": [
            { "name": "Infrastructure Setup", "description": "Provisioning and configuring cloud resources.", "technologies": ["AWS", "Terraform", "Docker"] },
            { "name": "Migration Tools", "description": "Tools for seamless data and application migration.", "technologies": ["AWS DMS", "Azure Migrate", "Google Transfer Appliance"] },
            { "name": "Monitoring Dashboard", "description": "Real-time monitoring of cloud resources and performance.", "technologies": ["CloudWatch", "Datadog", "Grafana"] }
        ],
    "images": ["https://media.istockphoto.com/id/1992282561/photo/digital-cloud-technology-in-your-hand-male-hand-holding-futuristic-cloud-chip-in-the-center.jpg?s=612x612&w=0&k=20&c=T1Zs8Z4M3ASbiWl3fi4S-ZS_Sd1xaCLU431rYMuKh78=", "https://media.istockphoto.com/id/1327776280/photo/connect-devices-to-cloud-businessman-or-information-technologist-with-cloud-computing-icon.jpg?s=612x612&w=0&k=20&c=nMhRoo61XybogxlraRqvGlZnWhxZFXltz_Peh-HIO4Q=","https://media.istockphoto.com/id/2012787753/photo/cloud-storage-technology-exchange-information-data-concept-digital-system-for-transferring.jpg?s=612x612&w=0&k=20&c=V1Hgct8EsR565hw4vXguVpKGLA_6ELLjGpoN9USXzLU="]
    },
    {
        "id": "svc_data_analytics_51b9a63c-9a5e-4b3c-a1e2-d4e2fbc92a77",
        "slug": "data-analytics",
        "name": "Data Analytics",
        "summary": "Extracting powerful insights from your data to drive strategic growth.",
        "hero": {
            "title": "Turn Data Into Actionable Intelligence",
            "pitch": "We help organizations uncover patterns, measure performance, and make smarter decisions using modern data analytics tools."
        },
        "icon": "icons/data-analytics.svg",
        "illustration": "images/services/data-analytics-hero.webp",
        "problemStatement": "Many businesses collect vast amounts of data but lack the tools or strategy to translate it into actionable insights that improve performance.",
        "valueProposition": [
            "Custom dashboards that visualize KPIs in real time",
            "Predictive analytics to forecast trends and outcomes",
            "Data storytelling that drives strategic decisions"
        ],
        "deliverables": [
            "Data audit & strategy report",
            "KPI framework and measurement plan",
            "Interactive dashboards & reports",
            "Data cleaning and transformation scripts",
            "Predictive & prescriptive analytics models",
            "Ongoing analytics support and reporting"
        ],
        "features": [
            { "title": "End-to-End Insights", "description": "From raw data to real business outcomes." },
            { "title": "Interactive Dashboards", "description": "Dynamic visualizations for decision-makers and teams." },
            { "title": "Predictive Analytics", "description": "Machine learning models that forecast future performance." },
            { "title": "Data Governance", "description": "Secure, accurate, and compliant data management." }
        ],
        "process": [
            { "step": 1, "name": "Discover", "description": "Identify goals, data sources, and key metrics.", "duration": "1 week" },
            { "step": 2, "name": "Collect & Prepare", "description": "Data gathering, cleaning, and structuring.", "duration": "1–2 weeks" },
            { "step": 3, "name": "Analyze", "description": "Statistical analysis, trend detection, and correlation modeling.", "duration": "2–3 weeks" },
            { "step": 4, "name": "Visualize", "description": "Build dashboards and present insights for decision-making.", "duration": "1–2 weeks" },
            { "step": 5, "name": "Optimize", "description": "Refine metrics and automate regular reporting.", "duration": "Ongoing" }
        ],
        "techStack": [
            { "category": "Data Processing", "tools": ["Python", "SQL", "Excel", "Power Query"] },
            { "category": "Analytics & Visualization", "tools": ["Tableau", "Power BI", "Google Data Studio", "Looker"] },
            { "category": "Big Data & Cloud", "tools": ["AWS Redshift", "Google BigQuery", "Snowflake"] },
            { "category": "Machine Learning", "tools": ["Scikit-learn", "TensorFlow", "Pandas", "NumPy"] }
        ],
        "pricingModel": "time_and_materials",
        "startingFrom": 4000,
        "timelineEstimate": { "minWeeks": 4, "maxWeeks": 8 },
        "teamRoles": ["Data Analyst", "Data Engineer", "BI Developer", "Statistician", "Project Manager"],
        "industries": ["Finance", "Retail", "Healthcare", "E-commerce", "Education"],
        "requirements": [
            "Access to relevant datasets or databases",
            "Defined KPIs and business goals",
            "Access to analytics tools or platform credentials",
            "Stakeholder alignment on reporting needs"
        ],
        "successMetrics": [
            "Decision-making speed ↑",
            "KPI visibility ↑",
            "Reporting efficiency ↑",
            "Revenue growth from data insights ↑"
        ],
        "caseStudies": ["cs_data_analytics_003", "cs_data_analytics_007"],
        "testimonials": ["t_data_analytics_01"],
        "faqs": [
            { "q": "Can you integrate with our existing CRM or ERP?", "a": "Yes, we can connect to most systems including Salesforce, HubSpot, and SAP for live analytics." },
            { "q": "Do you offer real-time dashboards?", "a": "Absolutely—our dashboards can refresh live data using cloud or API integrations." }
        ],
        "cta": { "label": "Start Your Data Project", "target": "/contact?service=data-analytics" },
        "seo": {
            "title": "Data Analytics Services | YourCompany",
            "metaDescription": "Uncover insights and optimize growth with expert data analytics, dashboards, and predictive modeling."
        },
        "schemaOrg": { "@type": "Service", "serviceType": "Data Analytics" },
        "relatedServices": ["machine-learning", "data-engineering", "business-intelligence"],
        "status": "active",
        "version": "1.0.0",
        "lastUpdated": "2025-10-27",
        "components": [
            { "name": "Data Pipeline", "description": "ETL processes for data ingestion and transformation.", "technologies": ["Python", "Apache Spark", "SQL"] },
            { "name": "Visualization Dashboard", "description": "Interactive charts and reports for KPIs.", "technologies": ["Tableau", "Power BI", "Plotly"] },
            { "name": "Predictive Model", "description": "Statistical models for forecasting and insights.", "technologies": ["Scikit-learn", "Pandas", "NumPy"] }
        ],
        "images": ["https://media.istockphoto.com/id/1867035079/photo/analytics-and-data-management-systems-business-analytics-and-data-management-systems-to-make.jpg?s=612x612&w=0&k=20&c=tFcJnBIWlkPhIumrPtkSJwFRNDMtdVfJ1CYbfUlx5fE=", "https://media.istockphoto.com/id/2148562711/photo/two-businesspeople-or-an-accountant-team-are-analyzing-data-charts-graphs-and-a-dashboard-on.jpg?s=612x612&w=0&k=20&c=TbJ5yeFFk3FEtIdzHPN40ebmlUwH4B0ukAEFiEo1gmM=","https://media.istockphoto.com/id/1480239160/photo/an-analyst-uses-a-computer-and-dashboard-for-data-business-analysis-and-data-management.jpg?s=612x612&w=0&k=20&c=Zng3q0-BD8rEl0r6ZYZY0fbt2AWO9q_gC8lSrwCIgdk="]
    }
]
